import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { I18nextProvider } from "react-i18next";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import i18n from "./i18n";
import "bootstrap/dist/css/bootstrap.min.css";

import { AuthProvider } from "./context/AuthContext";
import AnalyticsTracker from "./AnalyticsTracker";

// Common Components
import AppNavbar from "./components/pages/Navbar";
import Footer from "./components/pages/Footer";

// User Pages
import HomePage from "./components/pages/HomePage";
import LoginModal from "./components/pages/LoginModal";
import RegisterModal from "./components/pages/RegisterModal";
import RequestModal from "./components/pages/RequestModal";
import AboutUs from "./components/pages/AboutUs";
import ContactUs from "./components/pages/ContactUs";
import BlogSection from "./components/pages/BlogSection";
import AllProducts from "./components/pages/AllProducts";
import ProductDetail from "./components/pages/ProductDetail";
import BannersAndPromotions from "./components/pages/BannersAndPromotions";
import AttachReceiptPage from "./components/pages/AttachReceiptPage";
import GoodsReceivingForm from "./components/AdminDashboard/GoodsReceivingForm";
import UserDashboardLayout from "./components/pages/UserDashboardLayout";
import BlogSectionDetails from "./components/pages/BlogSectionDetails";
import QualityAssurance from "./components/pages/QualityAssurance";

// Admin Pages
import RawMaterialStockManagement from "./components/AdminDashboard/RawMaterialStockManagement";
import RawMaterialReceivingNote from "./components/AdminDashboard/RawMaterialReceivingNote";
import DashboardLayout from "./components/AdminDashboard/DashboardLayout";
import ProductsPage from "./components/AdminDashboard/ProductsPage";
import ProductStocks from "./components/AdminDashboard/ProductStocks";
import BannersPage from "./components/AdminDashboard/BannersPage";
import BlogPage from "./components/AdminDashboard/BlogPage";
import CustomerPage from "./components/AdminDashboard/CustomerPage";
import OrderPage from "./components/AdminDashboard/OrderPage";
import AdminPage from "./components/AdminDashboard/AdminPage";
import RequestPage from "./components/AdminDashboard/RequestPage";
import ReportsPage from "./components/AdminDashboard/ReportsPage";
import SettingsPage from "./components/AdminDashboard/SettingsPage";
import PerformaInvoicePage from "./components/AdminDashboard/PerformaInvoicePage";
import ViewReceiptPage from "./components/AdminDashboard/ViewReceiptPage";
import SalesManagement from "./components/AdminDashboard/SalesManagement";
import AnalyticsDashboard from "./components/AdminDashboard/AnalyticsDashboard";
import FinishedGoodsCustomerDeliveryForm from "./components/AdminDashboard/FinishedGoodsCustomerDeliveryForm";
import AdminRegisterModal from "./components/AdminDashboard/AdminRegisterModal";
import AdminLoginModal from "./components/AdminDashboard/AdminLoginModal";
import DashboardPage from "./components/AdminDashboard/DashboardPage";
import StoreIssueVoucher from "./components/AdminDashboard/StoreIssueVoucher";

// Other Dashboards
import StoreKeeperDashboardLayout from "./components/StoreKeeperDashboard/StoreKeeperDashboardLayout";
import MarketingDashboardLayout from "./components/MarketingDashboard/MarketingDashboardLayout";
import Productdescription from "./components/MarketingDashboard/Productdescription";
import ProductionDashboardLayout from "./components/ProductionDashboard/ProductionDashboardLayout";

/* =========================
   USER PRIVATE ROUTE
========================= */
const PrivateRoute = ({ children }) => {
  const authToken = localStorage.getItem("authToken");

  return authToken ? children : <Navigate to="/" replace />;
};

/* =========================
   ADMIN PRIVATE ROUTE
========================= */
const AdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem("adminToken");

  return adminToken ? children : <Navigate to="/admin-login" replace />;
};

/* =========================
   ADMIN REGISTER WRAPPER
========================= */
const AdminRegisterWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [show, setShow] = useState(
    location.pathname === "/admin-register"
  );

  useEffect(() => {
    if (!show) {
      navigate("/");
    }
  }, [show, navigate]);

  return (
    <AdminRegisterModal
      show={show}
      handleClose={() => setShow(false)}
    />
  );
};

/* =========================
   MAIN APP
========================= */
function App() {
  const [showAdminRegister, setShowAdminRegister] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  return (
    <AuthProvider>
      <I18nextProvider i18n={i18n}>
        <Router>
          <AppNavbar
            onAdminRegister={() => setShowAdminRegister(true)}
            onAdminLogin={() => setShowAdminLogin(true)}
          />

          <AnalyticsTracker />

          {/* Modals */}
          <AdminRegisterModal
            show={showAdminRegister}
            handleClose={() => setShowAdminRegister(false)}
          />

          <AdminLoginModal
            show={showAdminLogin}
            handleClose={() => setShowAdminLogin(false)}
          />

          <Routes>
            {/* =========================
                PUBLIC ROUTES
            ========================= */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginModal />} />
            <Route path="/register" element={<RegisterModal />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/all-products" element={<AllProducts />} />
            <Route path="/requests" element={<RequestModal />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/banners" element={<BannersAndPromotions />} />
            <Route
              path="/goods-receiving-form"
              element={<GoodsReceivingForm />}
            />
            <Route
              path="/deliveries"
              element={<FinishedGoodsCustomerDeliveryForm />}
            />
            <Route
              path="/submit-receipt"
              element={<AttachReceiptPage />}
            />
            <Route path="/blogs" element={<BlogSection />} />
            <Route
              path="/blogs/:id"
              element={<BlogSectionDetails />}
            />
            <Route
              path="/quality-assurance"
              element={<QualityAssurance />}
            />

            <Route
              path="/admin-register"
              element={<AdminRegisterWrapper />}
            />

            <Route
              path="/admin-login"
              element={
                <AdminLoginModal
                  show={true}
                  handleClose={() => window.history.back()}
                />
              }
            />

            {/* =========================
                USER PROTECTED ROUTES
            ========================= */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <UserDashboardLayout />
                </PrivateRoute>
              }
            />

            {/* =========================
                ADMIN PROTECTED ROUTES
            ========================= */}
            <Route
              path="/admin-dashboard"
              element={
                <AdminRoute>
                  <DashboardLayout />
                </AdminRoute>
              }
            />

            <Route
              path="/dashboard-page"
              element={
                <AdminRoute>
                  <DashboardPage />
                </AdminRoute>
              }
            />

            <Route
              path="/products-admin"
              element={
                <AdminRoute>
                  <ProductsPage />
                </AdminRoute>
              }
            />

            <Route
              path="/stock"
              element={
                <AdminRoute>
                  <ProductStocks />
                </AdminRoute>
              }
            />

            <Route
              path="/raw-material-stock"
              element={
                <AdminRoute>
                  <RawMaterialStockManagement />
                </AdminRoute>
              }
            />

            <Route
              path="/receiving-notes"
              element={
                <AdminRoute>
                  <RawMaterialReceivingNote />
                </AdminRoute>
              }
            />

            <Route
              path="/banners-admin"
              element={
                <AdminRoute>
                  <BannersPage />
                </AdminRoute>
              }
            />

            <Route
              path="/blog-admin"
              element={
                <AdminRoute>
                  <BlogPage />
                </AdminRoute>
              }
            />

            <Route
              path="/customers"
              element={
                <AdminRoute>
                  <CustomerPage />
                </AdminRoute>
              }
            />

            <Route
              path="/orders"
              element={
                <AdminRoute>
                  <OrderPage />
                </AdminRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />

            <Route
              path="/requests-admin"
              element={
                <AdminRoute>
                  <RequestPage />
                </AdminRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <AdminRoute>
                  <ReportsPage />
                </AdminRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <AdminRoute>
                  <SettingsPage />
                </AdminRoute>
              }
            />

            <Route
              path="/performa-invoice"
              element={
                <AdminRoute>
                  <PerformaInvoicePage />
                </AdminRoute>
              }
            />

            <Route
              path="/view-receipt"
              element={
                <AdminRoute>
                  <ViewReceiptPage />
                </AdminRoute>
              }
            />

            <Route
              path="/sales"
              element={
                <AdminRoute>
                  <SalesManagement />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/analytics"
              element={
                <AdminRoute>
                  <AnalyticsDashboard />
                </AdminRoute>
              }
            />

            {/* =========================
                OTHER DASHBOARDS
            ========================= */}
            <Route
              path="/manager-dashboard"
              element={
                <AdminRoute>
                  <MarketingDashboardLayout />
                </AdminRoute>
              }
            />

            <Route
              path="/expert-dashboard"
              element={
                <AdminRoute>
                  <StoreKeeperDashboardLayout />
                </AdminRoute>
              }
            />

            <Route
              path="/production_Stock_dashboard"
              element={
                <AdminRoute>
                  <ProductionDashboardLayout />
                </AdminRoute>
              }
            />

            <Route
              path="/product-description"
              element={
                <AdminRoute>
                  <Productdescription />
                </AdminRoute>
              }
            />

            <Route
              path="/store-issue-vouchers"
              element={
                <AdminRoute>
                  <StoreIssueVoucher />
                </AdminRoute>
              }
            />
          </Routes>

          <Footer />
        </Router>
      </I18nextProvider>
    </AuthProvider>
  );
}

ReactDOM.render(<App />, document.getElementById("root"));

export default App;